import { createCurrencyRate } from "./currencyRatesApi";
import { MAX_SEED_RATE, RATE_DECIMALS, SEED_PAIRS } from "./currencyRatesConstants";

/**
 * Formats a direction as a human label used in the seed report.
 * @param {string} base - currency the amount starts in
 * @param {string} quote - currency the amount is converted to
 * @returns {string}
 */
export function directionLabel(base, quote) {
    return `${base} → ${quote}`;
}

/**
 * Validates one direct rate typed in the quick seed form.
 * @param {string} value - raw input value
 * @returns {boolean} true when the rate and its inverse are both storable
 */
function isSeedRateValid(value) {
    const rate = Number(value);
    return Boolean(value) && !Number.isNaN(rate) && rate > 0 && rate < MAX_SEED_RATE;
}

/**
 * Builds the directional payloads for one direct rate: the rate itself, and
 * its arithmetic inverse as a separate row. The reverse is only a starting
 * point — each row is independent afterwards, so one side can be widened
 * without touching the other.
 * @param {Object} pair - entry of SEED_PAIRS
 * @param {string} value - direct rate typed by the admin
 * @param {string} margin - spread charged to the payer
 * @returns {Array} two API payloads
 */
function buildPairPayloads(pair, value, margin) {
    const rate = Number(value);
    return [
        {
            base_currency: pair.base, quote_currency: pair.quote,
            rate: String(rate), margin_percent: margin,
        },
        {
            base_currency: pair.quote, quote_currency: pair.base,
            rate: (1 / rate).toFixed(RATE_DECIMALS), margin_percent: margin,
        },
    ];
}

/**
 * Builds every payload of the quick seed, skipping the pairs left blank.
 * @param {Object} form - eurMga, usdMga and margin as strings
 * @returns {Array} list of API payloads
 */
export function buildSeedPayloads(form) {
    return SEED_PAIRS.filter((pair) => isSeedRateValid(form[pair.field]))
        .flatMap((pair) => buildPairPayloads(pair, form[pair.field], form.margin));
}

/**
 * Creates each payload in turn. An already configured pair answers 409 and is
 * reported as skipped rather than as a failure, exactly like the seeding
 * script, so the button stays safe to press twice.
 * @param {Array} payloads - rows to create
 * @returns {Promise<Object>} { created, skipped, failed } label lists
 */
export async function seedPairs(payloads) {
    const report = { created: [], skipped: [], failed: [] };
    for (const payload of payloads) {
        const label = directionLabel(payload.base_currency, payload.quote_currency);
        try {
            await createCurrencyRate(payload);
            report.created.push(label);
        } catch (error) {
            if (error?.response?.status === 409) report.skipped.push(label);
            else report.failed.push(label);
        }
    }
    return report;
}
